import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * ScrollManager — must live INSIDE <BrowserRouter>.
 *
 * This app scrolls on <body> (because `html,body { overflow-x: hidden }` makes
 * body's overflow-y compute to `auto`), NOT on the window. So we operate on the
 * real scroll container, detected at runtime.
 *
 * Behaviour:
 *   • PUSH/REPLACE to a new pathname  → scroll to top (or to a `#hash` target)
 *   • POP (browser Back/Forward)      → restore the scroll offset saved for that
 *                                       history entry (so Back lands you where
 *                                       you were, not at the top)
 *   • query/hash-only change          → leave scroll untouched
 */
function getScroller() {
  const b = document.body;
  if (b && b.scrollHeight > b.clientHeight + 4) return b;
  return document.scrollingElement || document.documentElement;
}

function setScroll(y) {
  const s = getScroller();
  try { s.scrollTop = y; } catch (_) { /* noop */ }
  try { window.scrollTo(0, y); } catch (_) { /* noop */ }
}

function getScroll() {
  return getScroller().scrollTop || window.scrollY || 0;
}

export function ScrollManager() {
  const { pathname, hash, key } = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const positions = useRef(new Map());
  const currentKey = useRef(key);
  const lastPath = useRef(pathname);
  const frozen = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Continuously record the scroll offset for the active history entry — but
  // freeze on pointer-down so the scroll-clamp that fires while the outgoing
  // page unmounts (Suspense fallback shrinks the body) can't overwrite the
  // good offset we want to restore on Back. We only UNFREEZE when the user
  // actively scrolls (wheel/touch/keys) or when a navigation commits (the
  // layout effect) — a click never fires those, so async navigations whose
  // nav() call is delayed are still protected.
  useEffect(() => {
    const save = () => positions.current.set(currentKey.current, getScroll());
    const onScroll = () => { if (!frozen.current) save(); };
    const onDown = () => { frozen.current = true; save(); };
    const onUserScroll = () => { if (frozen.current) { frozen.current = false; save(); } };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    document.addEventListener("pointerdown", onDown, { passive: true, capture: true });
    document.addEventListener("wheel", onUserScroll, { passive: true, capture: true });
    document.addEventListener("touchmove", onUserScroll, { passive: true, capture: true });
    document.addEventListener("keydown", onUserScroll, { capture: true });
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("scroll", onScroll, { capture: true });
      document.removeEventListener("pointerdown", onDown, { capture: true });
      document.removeEventListener("wheel", onUserScroll, { capture: true });
      document.removeEventListener("touchmove", onUserScroll, { capture: true });
      document.removeEventListener("keydown", onUserScroll, { capture: true });
    };
  }, []);

  useLayoutEffect(() => {
    if (hash) {
      currentKey.current = key;
      lastPath.current = pathname;
      frozen.current = false;
      return;
    }

    const samePath = lastPath.current === pathname;
    currentKey.current = key;
    lastPath.current = pathname;
    frozen.current = false; // new entry active — resume saving for it

    const applyY = (y) => {
      setScroll(y);
      const raf = requestAnimationFrame(() => setScroll(y));
      const t = setTimeout(() => setScroll(y), 250);
      return () => { cancelAnimationFrame(raf); clearTimeout(t); };
    };

    // Back / Forward — restore the saved position for this entry. Retry until
    // lazy below-the-fold sections mount (scrollHeight grows) so we can reach
    // the full saved offset, or a short deadline passes / the user scrolls.
    if (navType === "POP") {
      const y = positions.current.get(key) ?? 0;
      if (!y) { setScroll(0); return; }
      let cancelled = false;
      const deadline = Date.now() + 5000;
      const cleanup = () => {
        window.removeEventListener("wheel", cancel);
        window.removeEventListener("touchstart", cancel);
        window.removeEventListener("keydown", cancel);
      };
      function cancel() { cancelled = true; cleanup(); }
      window.addEventListener("wheel", cancel, { passive: true, once: true });
      window.addEventListener("touchstart", cancel, { passive: true, once: true });
      window.addEventListener("keydown", cancel, { once: true });
      const tryScroll = () => {
        if (cancelled) return;
        setScroll(y);
        if (Math.abs(getScroll() - y) > 2 && Date.now() < deadline) {
          setTimeout(tryScroll, 80);
        } else {
          cleanup();
        }
      };
      requestAnimationFrame(tryScroll);
      return () => { cancelled = true; cleanup(); };
    }

    // Query/hash-only change on the same page — leave scroll untouched.
    if (samePath) return;

    // New page — go to the top.
    return applyY(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Hash target scrolling — retry until element mounts (lazy pages).
  useEffect(() => {
    if (!hash) return;
    const target = hash.replace(/^#/, "");
    if (!target) return;
    let tries = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (++tries < 20) setTimeout(tick, 60);
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; };
  }, [pathname, hash]);

  return null;
}
